import Icon from "@common/controls/Icon/component";
import SkeletonPreloader from "@common/controls/Preloader/Skeleton/component";
import { IconType, type InputTypeProps } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import classNames from "classnames";
import { formatCreditCard, formatDate, getCreditCardType, unformatCreditCard } from "cleave-zen";
import * as React from "react";
import { TextInput } from "../Text";

import "./style.less";

type Props = InputTypeProps<CreditCardInfo | null>;

export type CreditCardInfo = {
  cardNumber: string;
  expirationDate: string;
  cardCode: string;
};

const EMPTY_VALUE = {
  cardNumber: "",
  expirationDate: "",
  cardCode: ""
};

const AVAILABLE_IMAGES = ["amex", "discover", "general", "maestro", "mastercard", "visa"];
const CreditCardInput = (props: Props) => {
  const i18n = useI18n();

  const brandImageSrc = React.useMemo(() => {
    if (!props.value?.cardNumber) return;

    let type = getCreditCardType(props.value.cardNumber).toLowerCase();
    if (!AVAILABLE_IMAGES.includes(type)) return;

    return `https://d1qgwakyzw6n5u.cloudfront.net/common/images/cards/${type}.png`;
  }, [props.value?.cardNumber]);

  const brandElement = React.useMemo(
    () => (brandImageSrc ? <img src={brandImageSrc} /> : <Icon icon={IconType.CreditCardFront} />),
    [brandImageSrc]
  );

  const numberElement = React.useMemo(
    () => (
      <TextInput
        placeholder={i18n.cardNumber}
        value={formatCreditCard(props.value?.cardNumber ?? "")}
        onChange={(value: string) =>
          props.onChange({ ...(props.value ?? EMPTY_VALUE), cardNumber: unformatCreditCard(formatCreditCard(value)) })
        }
      />
    ),
    [i18n, props.value, props.onChange]
  );

  const dateElement = React.useMemo(
    () => (
      <TextInput
        placeholder={"MM/YY"}
        value={props.value?.expirationDate ?? ""}
        onChange={(value: string) =>
          props.onChange({
            ...(props.value ?? EMPTY_VALUE),
            expirationDate: formatDate(value, {
              datePattern: ["m", "y"]
            })
          })
        }
      />
    ),
    [props.value, props.onChange]
  );

  const cvcElement = React.useMemo(
    () => (
      <TextInput
        maxLength={3}
        placeholder="CVC"
        onChange={(value: string) => {
          if (isNaN(+value)) return;

          props.onChange({ ...(props.value ?? EMPTY_VALUE), cardCode: value });
        }}
        value={props.value?.cardCode ?? ""}
      />
    ),
    [props.value, props.onChange]
  );

  return (
    <CreditCardInputContainer
      brandElement={brandElement}
      cvvElement={cvcElement}
      dateElement={dateElement}
      numberElement={numberElement}
      style={props.style}
    />
  );
};

export default CreditCardInput;

export type CreditCardInfoClasses = {
  number: string;
  date: string;
  cvv: string;
};
export type CreditCardInputContainerProps = {
  classes?: CreditCardInfoClasses;
  isLoading?: boolean;
  preloaderStyle?: React.CSSProperties;
  style?: React.CSSProperties;

  brandElement?: any;
  numberElement: any;
  dateElement: any;
  cvvElement: any;
};
export const CreditCardInputContainer = (props: CreditCardInputContainerProps) => (
  <div className="credit_card_input" style={props.style}>
    {!!props.brandElement && <div className="brand">{props.brandElement}</div>}
    <SkeletonPreloader
      className={classNames("card_input", props.classes?.number)}
      isLoading={props.isLoading}
      style={props.preloaderStyle}
    >
      {props.numberElement}
    </SkeletonPreloader>
    <SkeletonPreloader
      className={classNames("date_input", props.classes?.date)}
      isLoading={props.isLoading}
      style={props.preloaderStyle}
    >
      {props.dateElement}
    </SkeletonPreloader>
    <SkeletonPreloader
      className={classNames("code_input", props.classes?.cvv)}
      isLoading={props.isLoading}
      style={props.preloaderStyle}
    >
      {props.cvvElement}
    </SkeletonPreloader>
  </div>
);
